/**
 * A random (version 4) UUID, for ids the PHONE makes - WT1's tour photo ids.
 *
 * ⛔ Why not the `k-${Date.now()}` style fallback `useThread.ts` uses for its optimistic keys: those never
 *    leave the browser. A tour photo id is the PRIMARY KEY of `tour_photos` (a `uuid` column with no default),
 *    so Postgres rejects anything that is not a real UUID - and the phone-made id is what makes a retried
 *    upload land as ONE row, so it cannot be swapped for a server-made one either.
 *
 * `crypto.randomUUID` only exists in a SECURE context (https or localhost). The app opened over plain http -
 * a phone on the LAN hitting a dev machine - has `crypto.getRandomValues` but not `randomUUID`, so the
 * fallback builds the same RFC 4122 v4 layout from the same random source.
 */

type RandomSource = Pick<Crypto, 'getRandomValues'> & { randomUUID?: () => string };

const hex = (byte: number) => byte.toString(16).padStart(2, '0');

/** RFC 4122 section 4.4: 122 random bits, the version nibble set to 4 and the variant bits to 10xx. */
export const uuidV4FromBytes = (bytes: Uint8Array): string => {
  if (bytes.length !== 16) throw new Error('A UUID needs exactly 16 random bytes');
  const b = Uint8Array.from(bytes);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, hex).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

export const newUuid = (source: RandomSource = globalThis.crypto): string => {
  if (typeof source.randomUUID === 'function') return source.randomUUID();
  return uuidV4FromBytes(source.getRandomValues(new Uint8Array(16)));
};

export const isUuid = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
